import { type KeyboardEvent, type RefObject, useEffect, useRef } from 'react'

import type { Reading } from '../../api/search'

/**
 * The one field a search is typed into — and the wavy underline under what MyLib did not understand.
 *
 * **It is a real `<input>`.** Underneath it lies a second copy of the same text, in the same font, at the
 * same place, with nothing visible but the decorations: that is what draws the underline. The alternative
 * would be `contenteditable`, and everything a person expects of a text field — selecting, undo, the iOS
 * keyboard, dictation, a password manager keeping out of the way — would then have to be rebuilt by hand
 * and would be subtly wrong for ever. A mirrored underlay is a few lines of CSS and cannot be wrong.
 *
 * The ghost completion works the same way: `created:` stands grey behind the cursor while `cre` is typed,
 * in the mirror rather than in the field, so nothing that is not really in the field can be selected,
 * copied or submitted by accident. Tab takes it.
 */
export interface QueryFieldProps {
  readonly line: string
  /** How the service read the line. `null` before the first answer — nothing is marked until then. */
  readonly reading: Reading | null
  /** The rest of a filter key being typed, or `null`. Shown grey; Tab takes it. */
  readonly ghost: string | null
  readonly placeholder: string
  /** The field's name for a screen reader. It has no visible label: the whole surface is the search. */
  readonly label: string
  readonly inputRef: RefObject<HTMLInputElement | null>
  /** The list the arrow keys are walking, for a screen reader — hits or suggestions. */
  readonly listId: string
  readonly activeId: string | undefined
  readonly onChange: (line: string, caret: number) => void
  /** Called when the cursor moves without the text changing: a click, an arrow, a selection. */
  readonly onCaret: (caret: number) => void
  readonly onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
}

export function QueryField({
  line,
  reading,
  ghost,
  placeholder,
  label,
  inputRef,
  listId,
  activeId,
  onChange,
  onCaret,
  onKeyDown,
}: QueryFieldProps) {
  const mirror = useRef<HTMLDivElement>(null)

  /*
   * The underlay follows the field sideways. A long search line scrolls inside the input, and an
   * underline that stayed put would sit under the wrong words — which is worse than no underline at all.
   */
  useEffect(() => {
    const field = inputRef.current
    const under = mirror.current
    if (field === null || under === null) {
      return
    }
    const follow = () => {
      under.scrollLeft = field.scrollLeft
    }
    follow()
    field.addEventListener('scroll', follow)
    return () => {
      field.removeEventListener('scroll', follow)
    }
  }, [inputRef, line])

  return (
    <div className="query">
      <div className="query__mirror" ref={mirror} aria-hidden="true">
        {segmentsOf(line, reading).map((segment) => (
          <span
            key={`${segment.from}-${segment.to}`}
            className={segment.unreadable ? 'query__unreadable' : undefined}
          >
            {segment.text}
          </span>
        ))}
        {ghost !== null && <span className="query__ghost">{ghost}</span>}
      </div>
      <input
        ref={inputRef}
        className="query__input field__input"
        type="text"
        role="combobox"
        aria-expanded="true"
        aria-controls={listId}
        aria-activedescendant={activeId}
        aria-label={label}
        placeholder={placeholder}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        value={line}
        onChange={(event) => {
          onChange(event.target.value, event.target.selectionStart ?? event.target.value.length)
        }}
        onKeyUp={(event) => {
          onCaret(event.currentTarget.selectionStart ?? line.length)
        }}
        onClick={(event) => {
          onCaret(event.currentTarget.selectionStart ?? line.length)
        }}
        onKeyDown={onKeyDown}
      />
    </div>
  )
}

interface Segment {
  readonly from: number
  readonly to: number
  readonly text: string
  readonly unreadable: boolean
}

/**
 * The line cut into the parts that were understood and the parts that were not.
 *
 * The ranges come from the service, because the rules of the search language are the service's. Drawing
 * them is this side's whole share of that.
 */
function segmentsOf(line: string, reading: Reading | null): Segment[] {
  const marks = [...(reading?.unreadable ?? [])]
    .filter((range) => range.from < range.to && range.to <= line.length)
    .sort((one, other) => one.from - other.from)

  if (marks.length === 0) {
    return [{ from: 0, to: line.length, text: line, unreadable: false }]
  }

  const segments: Segment[] = []
  let at = 0
  marks.forEach((range) => {
    if (range.from > at) {
      segments.push({
        from: at,
        to: range.from,
        text: line.slice(at, range.from),
        unreadable: false,
      })
    }
    segments.push({
      from: range.from,
      to: range.to,
      text: line.slice(range.from, range.to),
      unreadable: true,
    })
    at = range.to
  })
  if (at < line.length) {
    segments.push({ from: at, to: line.length, text: line.slice(at), unreadable: false })
  }
  return segments
}
